import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createWorker } from "../services/worker";
import { getUserInfo, getSucursalInfo } from "../services/auth";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 4;

// Matches sge_infantil_web's workers/create validation exactly: BI is
// required with the Mozambican ID pattern, NUIT is required (no pattern),
// email is optional but format-checked when present.
const BI_PATTERN = /^[0-9]{10,}[A-Z]{1,1}$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const initialState = {
  name: "",
  biNumber: "",
  nuit: "",
  birthDate: "",
  maritalStatus: "Solteiro",
  gender: "",
  email: "",
  contact: "",
  educationalLevel: "Ensino Médio",
  degreeYear: "",
  institution: "",
  contractType: "",
  position: "",
};

const CadastrarFuncionarioLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const stepLabels = [
    t("funcionario.wizard.step.personal"),
    t("funcionario.wizard.step.degree"),
    t("funcionario.wizard.step.contract"),
    t("funcionario.wizard.step.confirm"),
  ];

  // name, biNumber, nuit, birthDate, gender and contact are required by the
  // original workers/create form; email is optional but format-validated.
  const canAdvanceFromStep1 =
    form.name && form.contact && form.birthDate && form.gender && form.biNumber && form.nuit;

  const nextStep = () => {
    if (currentStep === 1) {
      if (!canAdvanceFromStep1) {
        toast.error(t("validation.required"));
        return;
      }
      if (!BI_PATTERN.test(form.biNumber)) {
        toast.error(t("validation.bi.invalid"));
        return;
      }
      if (form.email && !EMAIL_PATTERN.test(form.email)) {
        toast.error(t("validation.email.invalid"));
        return;
      }
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const submit = async () => {
    setSubmitting(true);
    const userId = getUserInfo()?.id;
    const payload = {
      name: form.name,
      idNumber: form.biNumber,
      nuit: form.nuit,
      taxId: form.nuit,
      birthDate: form.birthDate,
      maritalStatus: form.maritalStatus,
      gender: form.gender,
      email: form.email,
      contact: form.contact,
      educationLevel: form.educationalLevel,
      lastEducationYear: form.degreeYear,
      institution: form.institution,
      contractType: form.contractType,
      duration: form.contractType,
      // The original form has a "position" select but never actually sends
      // it in the create payload (dead field in sge_infantil_web) — kept in
      // the UI for completeness, intentionally omitted here for fidelity.
      // isPermanent: the "belongs to QAD" field is commented out in the
      // original form too, so it's always submitted as "Nao".
      isPermanent: "Nao",
      active: "1",
      branchId: "Sede",
      country: "Moçambique",
      sucursalId: getSucursalInfo()?.id,
      createdBy: userId,
      activatedBy: userId,
      createdAt: new Date().toISOString(),
    };

    try {
      await createWorker(payload);
      setDone(true);
    } catch (err) {
      if (err?.response?.data?.error === "Worker with the same Nuit ID or ID Number already exists.") {
        toast.error(t("funcionario.error.duplicate"));
      } else {
        toast.error(t("global.error.message"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("funcionario.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("funcionario.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>
                        {t("funcionario.field.name")}*
                      </label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="f7:person" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t("funcionario.field.name.placeholder")}
                          value={form.name}
                          onChange={setField("name")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("funcionario.field.bi")} <span className='required-asterisk'>*</span></label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:card-2-outline" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t("funcionario.field.bi.placeholder")}
                          value={form.biNumber}
                          onChange={setField("biNumber")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("funcionario.field.nuit")} <span className='required-asterisk'>*</span></label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:document-text-outline" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t("funcionario.field.nuit.placeholder")}
                          value={form.nuit}
                          onChange={setField("nuit")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>
                        {t("funcionario.field.birthDate")} <span className='required-asterisk'>*</span>
                      </label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:calendar-outline" />
                        </span>
                        <input
                          type='date'
                          className='form-control'
                          value={form.birthDate}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={setField("birthDate")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>
                        {t("funcionario.field.maritalStatus")}
                      </label>
                      <select
                        className='form-control form-select'
                        value={form.maritalStatus}
                        onChange={setField("maritalStatus")}
                      >
                        <option value='Solteiro'>{t("global.single")}</option>
                        <option value='Casado'>{t("global.married")}</option>
                        <option value='Divorciado'>{t("global.divorced")}</option>
                        <option value='Viuvo'>{t("global.widower")}</option>
                      </select>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("funcionario.field.gender")} <span className='required-asterisk'>*</span></label>
                      <select
                        className='form-control form-select'
                        value={form.gender}
                        onChange={setField("gender")}
                      >
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        <option value='Masculino'>{t("global.male")}</option>
                        <option value='Femenino'>{t("global.female")}</option>
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("funcionario.field.email")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mage:email" />
                        </span>
                        <input
                          type='email'
                          className='form-control'
                          placeholder={t("funcionario.field.email.placeholder")}
                          value={form.email}
                          onChange={setField("email")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("funcionario.field.contact")} <span className='required-asterisk'>*</span></label>
                      <div className='input-group'>
                        <span className='input-group-text'>+258</span>
                        <input
                          type='text'
                          maxLength={9}
                          className='form-control'
                          value={form.contact}
                          onChange={setField("contact")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='form-group text-end mt-24'>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>
                        {t("funcionario.field.educationalLevel")}
                      </label>
                      <select
                        className='form-control form-select'
                        value={form.educationalLevel}
                        onChange={setField("educationalLevel")}
                      >
                        <option value='Ensino Médio'>{t("global.middle.school")}</option>
                        <option value='Ensino Superior'>{t("global.bachelor")}</option>
                        <option value='Pós-graduação'>{t("global.post.graduated")}</option>
                        <option value='Mestrado'>{t("global.master")}</option>
                      </select>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("funcionario.field.degreeYear")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mdi:school-outline" />
                        </span>
                        <input
                          type='number'
                          className='form-control'
                          placeholder={t("funcionario.field.degreeYear.placeholder")}
                          value={form.degreeYear}
                          onChange={setField("degreeYear")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-12'>
                      <label className='form-label'>{t("funcionario.field.institution")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:buildings-2-outline" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t("funcionario.field.institution.placeholder")}
                          value={form.institution}
                          onChange={setField("institution")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 3 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("funcionario.field.contractType")}</label>
                      <select
                        className='form-control form-select'
                        value={form.contractType}
                        onChange={setField("contractType")}
                      >
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        <option value='Tempo Parcial'>Tempo Parcial</option>
                        <option value='Tempo Integral'>Tempo Integral</option>
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("funcionario.field.position")}</label>
                      <select
                        className='form-control form-select'
                        value={form.position}
                        onChange={setField("position")}
                      >
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        <option value='Director Geral'>Director Geral</option>
                        <option value='Director Geral Adjunto'>Director Geral Adjunto</option>
                        <option value='Director Pedagogico'>Director Pedagógico</option>
                        <option value='Assistente Comercial'>Assistente Comercial</option>
                        <option value='Controlo Financeiro'>Controlo Financeiro</option>
                      </select>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 4 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("funcionario.confirm.description")}</div>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("global.personal.information")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("funcionario.field.name")}:</strong> {form.name}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.email")}:</strong> {form.email}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.contact")}:</strong> +258 {form.contact}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.birthDate")}:</strong> {form.birthDate}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.maritalStatus")}:</strong>{" "}
                          {form.maritalStatus}
                        </li>
                      </ul>
                    </div>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("global.degree.info")} / {t("global.contract.info")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("funcionario.field.educationalLevel")}:</strong>{" "}
                          {form.educationalLevel}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.degreeYear")}:</strong> {form.degreeYear}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.institution")}:</strong>{" "}
                          {form.institution}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.contractType")}:</strong>{" "}
                          {form.contractType}
                        </li>
                        <li>
                          <strong>{t("funcionario.field.position")}:</strong> {form.position}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={submit}
                      type='button'
                      className='form-wizard-submit btn btn-primary-600 px-32'
                      disabled={submitting}
                    >
                      {submitting ? (
                        "..."
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>
                      {t("funcionario.create.success.title")}
                    </h6>
                    <p className='text-neutral-400 text-sm mb-0'>
                      {t("funcionario.create.success.subtitle")}
                    </p>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button
                      type='button'
                      className='btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={restart}
                    >
                      {t("funcionario.create.new")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/funcionarios/lista")}
                    >
                      <i className='ri-arrow-left-line' /> {t("funcionario.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarFuncionarioLayer;
